/**
 * Event Form Component
 * 
 * Reusable form for creating and editing events with:
 * - Form validation
 * - Pre-population for edit mode
 * - Error handling
 * - Loading states
 * 
 * Requirements: 2.6, 2.7, 2.8, 12.4
 */

import { useEffect, useState } from 'react';
import { Event, CreateEventDto, UpdateEventDto, EventType, Location, TrailConfiguration, restClient } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { FormField } from '../../common/FormField';
import { RiderSelector } from './RiderSelector';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { required, validDate, url } from '../../../utils/validation';
import { parseError } from '../../../utils/errorHandling';

export interface EventFormProps {
  event?: Event;
  eventTypes: EventType[];
  locations: Location[];
  onSubmit: (data: CreateEventDto | UpdateEventDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormData {
  event_name: string;
  event_type: string;
  event_location: string;
  event_address: string;
  event_starttime: string;
  event_endtime: string;
  event_status?: 'active' | 'canceled';
  trail_configuration_id?: string;
  official_results_url?: string;
}

/**
 * Convert ISO datetime to datetime-local input format
 * Returns empty string if date is invalid (prevents NaN errors)
 */
function toDateTimeLocal(isoString: string): string {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('[EventForm] Invalid date string:', isoString);
    return '';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Convert datetime-local input to ISO string
 */
function toISOString(dateTimeLocal: string): string {
  return new Date(dateTimeLocal).toISOString();
}

/**
 * Get default start time (today at 6:30 PM)
 */
function getDefaultStartTime(): string {
  const now = new Date();
  now.setHours(18, 30, 0, 0); // 6:30 PM
  return toDateTimeLocal(now.toISOString());
}

/**
 * Get default end time (today at 8:00 PM)
 */
function getDefaultEndTime(): string {
  const now = new Date();
  now.setHours(20, 0, 0, 0); // 8:00 PM
  return toDateTimeLocal(now.toISOString());
}

/**
 * Build the submit DTO, conditionally including included_rider_ids.
 * When includedRiderIds is undefined, the field is omitted (all riders invited).
 * When it is a defined array, the field is included with exactly those IDs.
 *
 * Exported for property-based testing.
 * Requirements: 3.1, 3.2, 9.4, 12.4
 */
export function buildSubmitDto(
  baseData: CreateEventDto | UpdateEventDto,
  includedRiderIds: string[] | undefined
): CreateEventDto | UpdateEventDto {
  if (includedRiderIds !== undefined) {
    return { ...baseData, included_rider_ids: includedRiderIds };
  }
  // Omit included_rider_ids entirely — "all riders invited" convention
  const { included_rider_ids: _removed, ...rest } = baseData as CreateEventDto & { included_rider_ids?: string[] };
  return rest;
}

/**
 * Determine initial rider selection state from an event.
 * If event has included_rider_ids, return that array.
 * If event has no included_rider_ids (or no event), return undefined (all selected).
 *
 * Exported for property-based testing.
 * Requirements: 6.1, 6.2
 */
export function initializeRiderSelection(event?: Event): string[] | undefined {
  return event?.included_rider_ids;
}

/**
 * Event Form Component
 * Requirements: 2.6, 2.7, 2.8, 12.4
 */
export function EventForm({
  event,
  eventTypes,
  locations,
  onSubmit,
  onCancel,
  isLoading = false,
}: EventFormProps) {
  const isEditMode = !!event;
  const { handleError, showSuccess } = useErrorHandler();

  // Rider selection state — undefined means "all riders selected"
  // Requirements: 3.1, 3.2, 6.1, 6.2
  const [includedRiderIds, setIncludedRiderIds] = useState<string[] | undefined>(
    initializeRiderSelection(event)
  );

  // Trail configurations state
  const [trailConfigurations, setTrailConfigurations] = useState<TrailConfiguration[]>([]);
  const [loadingTrailConfigurations, setLoadingTrailConfigurations] = useState(false);
  const [trailConfigurationsError, setTrailConfigurationsError] = useState<string | null>(null);

  // Find the default location (Blankets Creek) by name, falling back to first location
  const defaultLocationId = (() => {
    const blankets = locations.find(loc => loc.name === 'Blankets Creek');
    if (blankets) return blankets.location_id;
    return locations.length > 0 ? locations[0].location_id : '';
  })();

  // Initial form values
  const initialValues: FormData = {
    event_name: event?.event_name || '',
    event_type: event?.event_type || 'Practice',
    event_location: event?.event_location || defaultLocationId,
    event_address: event?.event_address || '',
    event_starttime: event ? toDateTimeLocal(event.event_starttime) : getDefaultStartTime(),
    event_endtime: event ? toDateTimeLocal(event.event_endtime) : getDefaultEndTime(),
    event_status: event?.event_status || 'active',
    trail_configuration_id: event?.trail_configuration_id || '',
    official_results_url: event?.official_results_url || '',
  };

  // Validation rules
  // Requirements: 12.4
  const validationRules = {
    event_name: [required('Event name is required')],
    event_type: [required('Event type is required')],
    event_location: [required('Location is required')],
    event_starttime: [
      required('Start time is required'),
      validDate('Please enter a valid date and time'),
    ],
    event_endtime: [
      required('End time is required'),
      validDate('Please enter a valid date and time'),
    ],
    official_results_url: [url()],
  };

  // Form validation hook
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue,
    setErrors,
  } = useFormValidation({
    initialValues,
    validationRules,
    onSubmit: async (formData) => {
      try {
        // Additional validation: end time must be after start time
        if (formData.event_starttime && formData.event_endtime) {
          const start = new Date(formData.event_starttime);
          const end = new Date(formData.event_endtime);
          
          if (end <= start) {
            setErrors({ event_endtime: 'End time must be after start time' });
            return;
          }
        }

        // Generate event_id for new events, use existing for updates
        const eventId = isEditMode && event ? event.event_id : crypto.randomUUID();

        const submitData: CreateEventDto | UpdateEventDto = buildSubmitDto({
          event_id: eventId,
          event_name: formData.event_name.trim(),
          event_type: formData.event_type,
          event_location: formData.event_location,
          event_address: formData.event_address.trim() || undefined,
          event_starttime: toISOString(formData.event_starttime),
          event_endtime: toISOString(formData.event_endtime),
          event_status: isEditMode ? formData.event_status : 'active',
          trail_configuration_id: formData.trail_configuration_id || undefined,
          official_results_url: formData.official_results_url?.trim() || undefined,
        }, includedRiderIds);

        await onSubmit(submitData);
        showSuccess(isEditMode ? 'Event updated successfully' : 'Event created successfully');
      } catch (error) {
        const parsed = parseError(error);
        
        // Handle server-side validation errors
        if (parsed.fieldErrors) {
          const formErrors: Record<string, string> = {};
          for (const [field, messages] of Object.entries(parsed.fieldErrors)) {
            formErrors[field] = messages[0];
          }
          setErrors(formErrors);
        }
        
        handleError(error, 'EventForm');
        throw error;
      }
    },
    validateOnBlur: true,
    validateOnChange: false,
  });

  // Update form when event prop changes (for edit mode)
  useEffect(() => {
    if (event) {
      setFieldValue('event_name', event.event_name);
      setFieldValue('event_type', event.event_type);
      setFieldValue('event_location', event.event_location);
      setFieldValue('event_address', event.event_address || '');
      setFieldValue('event_starttime', toDateTimeLocal(event.event_starttime));
      setFieldValue('event_endtime', toDateTimeLocal(event.event_endtime));
      setFieldValue('event_status', event.event_status);
      setFieldValue('trail_configuration_id', event.trail_configuration_id || '');
      setFieldValue('official_results_url', event.official_results_url || '');
    }
  }, [event, setFieldValue]);

  // Fetch trail configurations on mount
  useEffect(() => {
    let cancelled = false;
    const fetchTrailConfigurations = async () => {
      try {
        setLoadingTrailConfigurations(true);
        setTrailConfigurationsError(null);
        const configs = await restClient.getTrailConfigurations();
        if (!cancelled) {
          setTrailConfigurations(configs);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[EventForm] Failed to fetch trail configurations:', error);
          setTrailConfigurationsError('Failed to load trail configurations');
        }
      } finally {
        if (!cancelled) {
          setLoadingTrailConfigurations(false);
        }
      }
    };

    fetchTrailConfigurations();
    return () => { cancelled = true; };
  }, []);

  // Set default location when locations load (for create mode)
  useEffect(() => {
    if (!isEditMode && locations.length > 0 && !locations.some(loc => loc.location_id === values.event_location)) {
      const blankets = locations.find(loc => loc.name === 'Blankets Creek');
      const defaultLoc = blankets || locations[0];
      setFieldValue('event_location', defaultLoc.location_id);
    }
  }, [isEditMode, locations, values.event_location, setFieldValue]);

  // Auto-populate address when location is selected
  useEffect(() => {
    if (values.event_location) {
      // Find location by ID (new format) or name (legacy format for backward compatibility)
      const selectedLocation = locations.find(loc => 
        loc.location_id === values.event_location || loc.name === values.event_location
      );
      if (selectedLocation?.address) {
        setFieldValue('event_address', selectedLocation.address);
      }
    }
  }, [values.event_location, locations, setFieldValue]);

  // Clear trail_configuration_id when event type changes from Time Trial
  useEffect(() => {
    if (values.event_type !== 'Time Trial' && values.trail_configuration_id) {
      setFieldValue('trail_configuration_id', '');
    }
  }, [values.event_type, values.trail_configuration_id, setFieldValue]);

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      {/* Event Name */}
      <FormField
        label="Event Name"
        name="event_name"
        type="text"
        value={values.event_name}
        error={errors.event_name}
        touched={touched.event_name}
        required
        placeholder="e.g., Tuesday Night Practice"
        onChange={handleChange('event_name')}
        onBlur={handleBlur('event_name')}
        disabled={isSubmitting || isLoading}
      />

      {/* Event Type */}
      <FormField
        label="Event Type"
        name="event_type"
        type="select"
        value={values.event_type}
        error={errors.event_type}
        touched={touched.event_type}
        required
        options={eventTypes.map(type => ({
          value: type.name,
          label: type.name,
        }))}
        onChange={handleChange('event_type')}
        onBlur={handleBlur('event_type')}
        disabled={isSubmitting || isLoading}
      />

      {/* Trail Configuration (Time Trial events only) */}
      {values.event_type === 'Time Trial' && (
        <FormField
          label="Trail Configuration"
          name="trail_configuration_id"
          type="select"
          value={values.trail_configuration_id || ''}
          options={[
            { value: '', label: 'None' },
            ...trailConfigurations.map(tc => ({
              value: tc.trail_id,
              label: `${tc.trail_name} (${tc.direction})${tc.is_default ? ' - Default' : ''}`
            }))
          ]}
          onChange={handleChange('trail_configuration_id')}
          disabled={isSubmitting || isLoading || loadingTrailConfigurations}
          helpText={
            trailConfigurationsError 
              ? trailConfigurationsError 
              : loadingTrailConfigurations 
                ? 'Loading trail configurations...' 
                : trailConfigurations.length === 0
                  ? 'No trail configurations available. Create one in Configuration.'
                  : 'Optional: Select a trail configuration for this event'
          }
        />
      )}

      {/* Official Results URL (Race events only) */}
      {values.event_type === 'Race' && (
        <FormField
          label="Official Results URL"
          name="official_results_url"
          type="text"
          value={values.official_results_url || ''}
          error={errors.official_results_url}
          touched={touched.official_results_url}
          placeholder="https://example.com/race-results"
          onChange={handleChange('official_results_url')}
          onBlur={handleBlur('official_results_url')}
          disabled={isSubmitting || isLoading}
          helpText="Optional: Link to official race results"
        />
      )}

      {/* Location */}
      <FormField
        label="Location"
        name="event_location"
        type="select"
        value={values.event_location}
        error={errors.event_location}
        touched={touched.event_location}
        required
        options={locations.map(location => ({
          value: location.location_id,  // Use location_id instead of name
          label: location.name,
        }))}
        onChange={handleChange('event_location')}
        onBlur={handleBlur('event_location')}
        disabled={isSubmitting || isLoading}
      />

      {/* Address */}
      <FormField
        label="Address"
        name="event_address"
        type="text"
        value={values.event_address}
        placeholder="Select a location to populate address"
        onChange={handleChange('event_address')}
        onBlur={handleBlur('event_address')}
        disabled={true}
        helpText="Auto-populated from selected location"
      />

      {/* Start Time */}
      <FormField
        label="Start Time"
        name="event_starttime"
        type="datetime-local"
        value={values.event_starttime}
        error={errors.event_starttime}
        touched={touched.event_starttime}
        required
        onChange={handleChange('event_starttime')}
        onBlur={handleBlur('event_starttime')}
        disabled={isSubmitting || isLoading}
      />

      {/* End Time */}
      <FormField
        label="End Time"
        name="event_endtime"
        type="datetime-local"
        value={values.event_endtime}
        error={errors.event_endtime}
        touched={touched.event_endtime}
        required
        onChange={handleChange('event_endtime')}
        onBlur={handleBlur('event_endtime')}
        disabled={isSubmitting || isLoading}
      />

      {/* Rider Selection */}
      <RiderSelector
        selectedRiderIds={includedRiderIds}
        season={new Date().getFullYear().toString()}
        onSelectionChange={setIncludedRiderIds}
      />

      {/* Status (Edit mode only) */}
      {isEditMode && (
        <FormField
          label="Status"
          name="event_status"
          type="select"
          value={values.event_status || 'active'}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'canceled', label: 'Canceled' },
          ]}
          onChange={handleChange('event_status')}
          disabled={isSubmitting || isLoading}
        />
      )}

      {/* Form Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || isLoading}
        >
          {isSubmitting || isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>{isEditMode ? 'Update Event' : 'Create Event'}</>
          )}
        </Button>
      </div>
    </form>
  );
}

export default EventForm;
